
import { BehaviorSubject, from as observableFrom, interval as observableInterval, merge as observableMerge,  Observable ,  ReplaySubject ,  Subscription ,  timer as observableTimer } from "rxjs";

import { AfterContentInit, ChangeDetectorRef, Directive, EventEmitter, Inject, InjectionToken, Input, OnDestroy, Optional, Output, SkipSelf } from "@angular/core";
import { NgModel } from "@angular/forms";
import { BooleanAttribute } from "@hex/angular/decorators";
import { get as _get, isEqual } from "lodash";
import { delay, distinctUntilChanged, filter, first, map, takeUntil, withLatestFrom } from "rxjs/operators";
import { NSFormFields } from "../../types";
import { BreakLengthValidator } from "./breaklength-validator";
import { FieldStateService } from "./field-state.service";
import { CommonFormManagerDirective } from "./form-manager.directive";
import { RuntimeValidator } from "./runtime-validator"
import { ContextManager, initGenericDropdownFieldState, initGenericFieldState, initGenericOptionsFieldState } from "./utils";

export const FIELD_TOKEN = new InjectionToken<string>("FIELD_TOKEN");

type FieldType =
    "date" | "text" | "number" | "float" |
    "textarea" | "runtime" | "breakLength" | "switch" |
    "dropdown" | "filteredDropdown" | "typeahead" | "checkboxSwitch" | "date";

@Directive({
    selector: "[common-field-manager]",
    exportAs: "FieldManager",
})
export class CommonFieldManagerDirective implements OnDestroy, AfterContentInit {
    @Input() public fieldProp: string;
    @Input("fieldLabel") public fieldLabelInput: string;
    @Input() public fieldHint: string;
    @Input() public fieldType: FieldType;
    @Input() public fieldDefault: any;
    @Input() public fieldPlaceholder: string;
    @Input() public fieldOptions: NSFormFields.IOptionsState[];
    @Input() public fieldOptionsIdPath = "id";
    @Input() public fieldOptionsLabelPath = "label";
    @Input() public fieldOptionsCategory: string;
    @Input() public fieldGroupContext: any;
    @Input() public fieldMin: any;
    @Input() public fieldMax: any;
    @Input() public fieldLabelWidth: string;
    @Input() @BooleanAttribute() public rightAlignLabel: boolean;
    @Input() @BooleanAttribute() public inlineField: boolean;
    @Input() @BooleanAttribute() public expandToFitSpace: boolean;
    public fieldIsDisabled = false;
    private fieldsWithDefaultValueZero: any = [
        "breakCount",
        "mpaaDisclaimerNumber",
    ];

    @Input("fieldIsDisabled")
    public set fieldIsDisabledInput(value) {
        this.fieldIsDisabled = value;
        this.changeDetectorRef.markForCheck();
    }

    @Input() public modSmallRightMargin = true;
    @Input() public modSmallTopMargin = true;
    @Input() public fieldTabIndex: number;

    public _fieldIsRequired: boolean;

    @Input("fieldIsRequired")
    @BooleanAttribute()
    public set fieldIsRequiredInput(value: boolean) {
        if (value !== false) {
            value = true;
        }

        if (this.fieldType !== "switch") {
            this._fieldIsRequired = value;
        }
    }

    public get fieldIsRequired(): boolean {
        if (this.fieldIsDisabled) {
            return false;
        }
        return this._fieldIsRequired;
    }

    @Input()
    @BooleanAttribute()
    public set focusOnInit(value) {
        this.isAutoFocused = !!value && !!this.fieldGroupContext.isManuallyAddedGroup;
    }

    @Input() public ngModel: NgModel;
    @Output() public fieldValueChange: EventEmitter<any> = new EventEmitter();

    public get fieldSize() {
        return !!this.inlineField ? "compact" : undefined;
    }

    public get fieldPropPath() {
        return this.fieldProp.split("::");
    }

    public context: ContextManager;
    public stateInitialized = false;
    public fieldValue: string | null;
    public selectedFieldValue: string;
    public selectedFieldLabel: string;
    public fieldErrorMessage: string;
    public initialState: any;
    public defaultValuesSynced = false;
    public touched = false;
    public isAutoFocused = false;

    public fieldState$: Observable<any>;
    public fieldValueFromStore$: Observable<any>;
    public synchronizeState$: Observable<any>;
    public detectChanges$: Observable<any>;

    public fieldStateSubscription: Subscription;
    public fieldValueFromStoreSubscription: Subscription;
    public synchronizeStateSubscription: Subscription;
    public detectChangesSubscription: Subscription;

    public fieldOptionsLoadedSubject = new ReplaySubject();
    public fieldOptionsLoaded$ = observableFrom(this.fieldOptionsLoadedSubject).pipe(filter((loaded) => !!loaded), first());
    public isAfterViewInitSubject = new ReplaySubject();
    public isAfterViewInit$ = observableFrom(this.isAfterViewInitSubject).pipe(
        filter((afterViewInit) => !!afterViewInit),
        first());

    constructor(@Inject(FIELD_TOKEN) public fieldToken,
                @SkipSelf() public formManager: CommonFormManagerDirective,
                public fieldStateService: FieldStateService,
                @Optional() public changeDetectorRef: ChangeDetectorRef,
                public breakLengthValidator: BreakLengthValidator,
                public runtimeValidator: RuntimeValidator) {
        this.fieldStateService.ngOnInit();
    }

    public ngAfterContentInit(): void {
        this.context = new ContextManager(
            this.formManager,
            this.fieldProp,
            this.fieldToken,
            this.fieldIsRequired,
            this.fieldType,
        );

        this.formManager.registerField(this);

        this.initialState = this.getInitialState();
        this.fieldStateService.stateObservable$ = new BehaviorSubject(this.initialState);
        this.fieldState$ = this.fieldStateService.stateObservable$.pipe(
            distinctUntilChanged((stateA, stateB) => isEqual(stateA, stateB)));
        this.fieldStateSubscription = this.fieldState$
            .subscribe((state) => {
                if (this.fieldValue !== state.value) {
                    this.dispatchNgModelChange(state.value);
                }

                this.isAfterViewInitSubject.next(true);
                this.stateInitialized = !!state;
                this.fieldValue = state.value;
                this.fieldLabelInput = state.label;
                this.fieldHint = state.hint;
                this.fieldPlaceholder = state.placeholder;
                this.selectedFieldValue = state.selectedValue;
                this.selectedFieldLabel = state.selectedLabel;
                this.touched = state.touched;
                this.fieldErrorMessage = state.errorMessage;

                if (!!state.options) {
                    this.fieldOptions = state.options;
                    this.fieldOptionsLoadedSubject.next(true);
                }
            });

        this.fieldValueFromStore$ = this.formManager.stateReplaySubject$.pipe(
            map((state: any) => _get(state, ["formData", ...this.fieldPropPath])),
            distinctUntilChanged((stateA, stateB) => isEqual(stateA, stateB)));

        this.fieldValueFromStoreSubscription = this.fieldValueFromStore$
            .subscribe((fieldValueFromStore) => {
                this.dispatchFieldUpdate(fieldValueFromStore);
            });

        this.synchronizeState$ = this.fieldValueFromStore$.pipe(withLatestFrom(this.fieldState$));
        this.synchronizeStateSubscription = this.synchronizeState$.pipe(
            delay(1),
            takeUntil(observableTimer(5000)),
            map(([fieldValueFromStore, fieldState]) => {
                return { fieldValueFromStore, fieldState };
            }))
            .subscribe((combinedState: any) => {
                this.syncDefaultValues();
                this.updateDataIfExists(combinedState.fieldValueFromStore);
            });

        this.detectChanges$ = observableMerge(
            this.fieldValueFromStore$,
            this.fieldState$,
            observableInterval(1000).pipe(takeUntil(observableTimer(10000))),
        );

        this.detectChangesSubscription = this.detectChanges$
            .subscribe((state) => this.changeDetectorRef.detectChanges());

        this.fieldStateService.ngAfterViewInit();
    }

    public ngOnDestroy() {
        this.fieldStateService.ngOnDestroy();
        this.formManager.unregisterField(this);
        if (this.detectChangesSubscription) {
            this.detectChangesSubscription.unsubscribe();
        }
        if (this.synchronizeStateSubscription) {
            this.synchronizeStateSubscription.unsubscribe();
        }
        if (this.fieldStateSubscription) {
            this.fieldStateSubscription.unsubscribe();
        }
        if (this.fieldValueFromStoreSubscription) {
            this.fieldValueFromStoreSubscription.unsubscribe();
        }
    }

    public setDefaultPlaceholder(): string {
        if (this.fieldPlaceholder) {
            return this.fieldPlaceholder;
        } else {
            const defaults = {
                text: "e.g. Disney",
                number: "e.g. 1234",
                float: "e.g. 1.5",
                date: "MM/DD/YYYY",
                runtim: "000:00",
                dropdown: "Select",
                filteredDropdown: "Select",
                fileUpload: "Attach File",
                multiselect: "Choose One",
            };
            return defaults[this.fieldType] || "";
        }
    }

    public setDefaultHint(): string {
        if (this.fieldHint) {
            return this.fieldHint;
        } else {
            return "";
        }
    }

    public getInitialState() {
        if (this.fieldType.match(/^date$|^text$|^number|^float|^runtime$|^textarea$/)) {
            return initGenericFieldState(
                this.fieldDefault,
                this.fieldLabel,
                this.setDefaultHint(),
                this.setDefaultPlaceholder(),
            );
        } else if (this.fieldType.match(/^radio$|^checkbox$/)) {
            return initGenericOptionsFieldState(
                this.fieldDefault,
                this.fieldLabel,
                this.setDefaultHint(),
                this.fieldOptions,
            );
        } else {
            return initGenericDropdownFieldState(
                this.fieldDefault,
                this.fieldLabel,
                this.setDefaultHint(),
                this.fieldOptions,
                this.setDefaultPlaceholder(),
            );
        }
    }

    public updateDataIfExists(value) {
        this.dispatchFieldUpdate(value);
    }

    public addDefaultValueIfProvided() {
        if (this.fieldDefault !== undefined) {
            this.dispatchFormFieldChange(this.fieldDefault);
        }
    }

    public syncDefaultValues() {
        if (this.stateInitialized && !this.defaultValuesSynced && !this.formManager.isEditForm && !this.formManager.isDuplicated) {
            this.addDefaultValueIfProvided();
            this.defaultValuesSynced = true;
        }
    }

    public dispatchFormFieldChange(newValue: any) {
        this.formManager.onFormFieldChange(
            this.fieldProp,
            newValue,
            { groupContext: this.fieldGroupContext },
        );
    }

    public get fieldLabel() {
        if (!!this.fieldLabelInput && this.fieldIsRequired && this.fieldLabelInput[this.fieldLabelInput.length - 1] !== "*") {
            return this.fieldLabelInput + "*";
        } else {
            return this.fieldLabelInput;
        }
    }

    public touch() {
        if (!this.stateInitialized) {
            return;
        }

        if (!this.touched) {
            this.fieldStateService.dispatchFieldTouched();
        }

        const errorMessage = this.buildErrorMessage();
        if (this.fieldErrorMessage !== errorMessage) {
            this.fieldStateService.dispatchFieldError(errorMessage);
            this.formManager.dispatchClientError(this.fieldProp, this.fieldLabel, errorMessage);
        }
    }

    public get isInvalid() {
        return this.validationFails();
    }

    public validationFails() {
        const invalid = this.buildErrorMessage() !== "";
        if (invalid && this.touched) {
            this.touch();
        } else if (this.fieldType === "dropdown" && this.touched) {
            this.touch();
        }
        return invalid;
    }

    public passesRequiredValidation() {
        if (this.fieldIsRequired) {
            if (this.fieldProp === "licensedServiceId") {
                return Boolean(this.selectedFieldValue);
            }
            if (this.fieldType === "runtime" && this.fieldValue === "000:00") {
                return true;
            }
            if (this.fieldType === "number" && this.fieldValue !== null && this.fieldValue !== undefined ) {
                if (this.fieldsWithDefaultValueZero.includes(this.fieldProp) && this.fieldValue.toString() === "0") {
                    return true;
                }
            }

            return Boolean(this.fieldValue);
        }

        return true;
    }

    public passesNumberValidation() {
        if (this.fieldType === "number" && !!this.fieldValue) {
            const regex = new RegExp("^[0-9]*$");
            return regex.test(this.fieldValue);
        } else {
            return true;
        }
    }

    public passesBelowMaxValidation() {
        if (!!this.fieldMax && !!this.fieldValue) {
            if (this.fieldType === "number" || this.fieldType === "float") {
                return parseFloat(this.fieldMax) >= parseFloat(this.fieldValue);
            } else if (this.fieldType === "date") {
                return new Date(this.fieldMax) >= new Date(this.fieldValue);
            } else {
                return this.fieldMax >= this.fieldValue;
            }
        } else {
            return true;
        }
    }

    public passesAboveMinValidation() {
        if (!!this.fieldMin && !!this.fieldValue) {
            if (this.fieldType === "number" || this.fieldType === "float") {
                return parseFloat(this.fieldMin) <= parseFloat(this.fieldValue);
            } else if (this.fieldType === "date") {
                return new Date(this.fieldMin) <= new Date(this.fieldValue);
            } else {
                return this.fieldMax <= this.fieldValue;
            }
        } else {
            return true;
        }
    }

    public resetState() {
        this.fieldStateService.dispatchStateReset(this.initialState);
    }

    public resetFieldValue() {
        this.formManager.resetFieldState(this.fieldProp);
    }

    public loadData(fieldValue) {
        if (this.context.isOptionsField) {
            this.fieldOptionsLoaded$.subscribe((state) => {
                this.dispatchFieldLoad(fieldValue);
            });
        } else {
            this.dispatchFieldLoad(fieldValue);
        }
    }

    public dispatchFieldUpdate(fieldValue, index?) {
        if (this.context.isOptionsField) {
            this.fieldOptionsLoaded$.subscribe(() => {
                this.fieldStateService.dispatchFieldUpdate(fieldValue, index);
            });
        } else {
            this.fieldStateService.dispatchFieldUpdate(fieldValue, index);
        }
    }

    public dispatchFieldLoad(payload) {
        this.fieldStateService.dispatchFieldLoad(payload);
    }

    public dispatchListLoad(payload) {
        this.fieldStateService.dispatchListLoad(payload, this.fieldOptionsLabelPath, this.fieldOptionsIdPath);
    }

    public buildErrorMessage(): string {
        if (!this.passesRequiredValidation()) {
            return "This field is required!";
        }

        if (this.fieldType === "breakLength") {
            return this.breakLengthValidator.validate(this.fieldValue) || "";
        }

        if (this.fieldType === "runtime") {
            return this.runtimeValidator.validate(this.fieldValue) || "";
        }

        const validations = [
            { message: "Only valid whole numbers allowed!", validation: this.passesNumberValidation },
            { message: `Input must be above ${this.fieldMin}`, validation: this.passesAboveMinValidation },
            { message: `Input must be below ${this.fieldMax}`, validation: this.passesBelowMaxValidation },
            { message: "Only valid dates mm/dd/yyyy allowed!", validation: this.passesDateValidation },
        ];

        const matchingValidation = validations.find((obj) => obj.validation.bind(this)() === false);
        return matchingValidation ? matchingValidation.message : "";
    }

    public next(newState) {
        this.fieldStateService.stateObservable$.next(newState);
    }

    public removeFromForm() {
        this.formManager.removeFieldFromForm(this.fieldProp);
    }

    public dispatchNgModelChange(e) {
        this.fieldValueChange.emit(e);
    }

    public passesDateValidation() {
        if (this.fieldType === "date" && !!this.fieldValue) {
            const regex = new RegExp("^(0[1-9]|1[0-2])\\/(0[1-9]|1\\d|2\\d|3[01])\\/(19|20)\\d{2}$");
            return regex.test(this.fieldValue);
        } else {
            return true;
        }
    }
}
